import { useState, useEffect } from "react";
import { Gift, Heart, SkipForward, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, money, fmtDate, formatApiError } from "../lib/api";
import { useTripTypes, earliestDate, latestDate, windowText } from "../lib/tripTypes";

export const OfferCard = ({ offer, charities, defaultSchool, settings, onDone }) => {
  const [charity, setCharity] = useState("");
  const [busy, setBusy] = useState(false);
  const tt = useTripTypes()[offer.vacation_trip_type || "weekend"];
  const minD = earliestDate(tt?.min || 1);
  const [date, setDate] = useState("");
  useEffect(() => {
    if (charities.length && !charity) setCharity(charities.some((c) => c.id === defaultSchool) ? defaultSchool : charities[0].id);
  }, [charities, charity, defaultSchool]);
  const total = offer.amount + offer.extra_amount;

  const act = async (kind) => {
    const msgs = {
      accept: "Enjoy your trip! Your scholarship share has been sent.",
      donate: `You donated ${money(total)} — thank you!`,
      pass: "You passed. You've moved back 3 places in line.",
    };
    if (kind !== "accept" && !window.confirm(kind === "pass" ? "Pass on this trip and move back 3 places?" : "Forfeit your trip and donate the full value?")) return;
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/${kind}`, kind === "pass" ? {} : kind === "accept" ? { charity_id: charity, travel_date: date } : { charity_id: charity });
      toast.success(msgs[kind]);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="offer-card" className="mt-8 rounded-2xl overflow-hidden border border-[#38BDF8]/40 grid md:grid-cols-5 bg-[#13192B]">
      {offer.vacation_image && <img src={offer.vacation_image} alt={offer.vacation_title} className="md:col-span-2 h-56 md:h-full w-full object-cover" />}
      <div className="md:col-span-3 p-7 md:p-9">
        <p className="eyebrow flex items-center gap-2"><Gift className="w-3.5 h-3.5" />You're up — your trip is ready</p>
        <h2 data-testid="offer-title" className="font-serif-lux text-3xl md:text-4xl mt-3">{offer.vacation_title}</h2>
        <p className="text-slate-400 text-sm mt-1">{offer.vacation_location} · {settings.label} · {({ solo: 1, duo: 2, family: 4 })[offer.plan] + (offer.extra_people || 0)} travelers</p>
        <div className="mt-5 flex flex-wrap gap-6 font-mono-lux text-sm">
          <div><p className="text-slate-500 text-xs">Trip value</p><p data-testid="offer-total" className="text-[#38BDF8] text-xl">{money(total)}</p></div>
          {offer.extra_amount > 0 && <div><p className="text-slate-500 text-xs">Your extra fund</p><p className="text-xl">+{money(offer.extra_amount)}</p></div>}
          <div><p className="text-slate-500 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Decide by</p><p data-testid="offer-deadline" className="text-xl">{fmtDate(offer.expires_at)}</p></div>
        </div>
        <label className="block mt-6">
          <span className="text-xs text-slate-400">School or fund to receive this cycle's club gift</span>
          <select data-testid="offer-charity-select" value={charity} onChange={(e) => setCharity(e.target.value)} className="lux-input mt-2">
            {charities.map((c) => <option key={c.id} value={c.id}>{`${c.name}${c.city ? ` — ${c.city}${c.state ? `, ${c.state}` : ""}` : ""}`}</option>)}
          </select>
        </label>
        <label className="block mt-4">
          <span className="text-xs text-slate-400">Travel date · {tt?.label} ({windowText(tt)}, so partners can book in time)</span>
          <input data-testid="offer-travel-date" type="date" min={minD} max={latestDate()} value={date} onChange={(e) => setDate(e.target.value)} className="lux-input mt-2" />
        </label>
        <p className="text-xs text-slate-500 mt-3">No choice by the deadline = trip auto-accepted and the scholarship goes to the school you picked when you joined (we'll contact you to set dates).</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button data-testid="offer-accept-btn" disabled={busy || !charity || !date || date < minD} onClick={() => act("accept")} className="btn-gold px-6 py-3 inline-flex items-center gap-2"><Gift className="w-4 h-4" />Accept trip</button>
          <button data-testid="offer-pass-btn" disabled={busy} onClick={() => act("pass")} className="btn-ghost px-5 py-3 text-sm inline-flex items-center gap-2"><SkipForward className="w-4 h-4" />Pass (back 3 spots)</button>
          <button data-testid="offer-donate-btn" disabled={busy || !charity} onClick={() => act("donate")} className="btn-ghost px-5 py-3 text-sm inline-flex items-center gap-2 text-rose-300"><Heart className="w-4 h-4" />Donate trip value</button>
        </div>
      </div>
    </div>
  );
};
